import { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  Fade,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";

import { getSaleProduct } from "../api/saleProductsApi";
import SaleProductRecipeDetailModal from "./SaleProductRecipeDetailModal";
import {
  saleProductCostStatusLabel,
  saleProductReviewReasonsLabel,
} from "../utils/saleProductStatus.utils";
import {
  formatCurrency,
  formatDecimalInput,
  normalizeSectors,
  UNIT_LABELS,
} from "../utils/saleProductForm.utils";

function errorToMessage(error) {
  const message =
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.data?.message ||
    error?.message;
  return Array.isArray(message)
    ? message.join(" | ")
    : message || "No se pudo cargar el detalle del producto.";
}

function quantityLabel(quantity, unit) {
  const value = formatDecimalInput(quantity);
  const unitLabel = UNIT_LABELS[unit] || unit || "";
  return [value, unitLabel].filter(Boolean).join(" ");
}

function sectorLabel(sectors) {
  const normalized = normalizeSectors(sectors);
  return normalized.length ? normalized.join(" y ") : "—";
}

function dateLabel(value) {
  if (!value) return "Sin cálculo registrado";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sin cálculo registrado";
  return new Intl.DateTimeFormat("es-AR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

function SectionTitle({ children }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 17, fontWeight: 800, color: "primary.900" }}>
        {children}
      </Typography>
    </Box>
  );
}

function DataCard({ label, value }) {
  return (
    <Box
      sx={{
        minWidth: 0,
        p: 1.4,
        borderRadius: "16px",
        bgcolor: "secondary.50",
      }}
    >
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "neutral.600" }}>
        {label}
      </Typography>
      <Typography
        noWrap
        title={String(value)}
        sx={{ mt: 0.25, fontSize: 13.5, fontWeight: 800, color: "primary.900" }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function StatePill({ positive, children }) {
  return (
    <Box
      sx={{
        minWidth: 82,
        px: 1.2,
        py: 0.55,
        borderRadius: "20px",
        bgcolor: positive
          ? "rgba(33, 150, 83, 0.16)"
          : "rgba(195, 60, 60, 0.16)",
        boxShadow: "inset 0px 0px 3px rgba(142,142,159,.22)",
      }}
    >
      <Typography
        sx={{
          color: positive ? "rgba(18, 111, 63, 1)" : "rgba(160, 49, 49, 1)",
          fontSize: 11,
          fontWeight: 800,
          textAlign: "center",
          whiteSpace: "nowrap",
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function ContentRow({ name, detail, type, action }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        minHeight: 54,
        px: 1.7,
        borderBottom: "1px dashed rgba(64,64,94,.2)",
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={0.7} sx={{ alignItems: "center" }}>
          <Typography
            noWrap
            title={name}
            sx={{ fontSize: 13.5, fontWeight: 800, color: "primary.900" }}
          >
            {name}
          </Typography>
          <Typography
            sx={{
              flexShrink: 0,
              px: 0.8,
              py: 0.2,
              borderRadius: "12px",
              bgcolor: "neutral.200",
              color: "neutral.700",
              fontSize: 9.5,
              fontWeight: 800,
            }}
          >
            {type}
          </Typography>
        </Stack>
        <Typography sx={{ mt: 0.2, fontSize: 10.5, fontWeight: 600, color: "neutral.600" }}>
          {detail}
        </Typography>
      </Box>
      {action}
    </Box>
  );
}

function ResolvedIngredientRow({ ingredient }) {
  const purchaseItem = ingredient.purchaseItem;
  const itemName = purchaseItem
    ? [purchaseItem.brand, purchaseItem.presentation].filter(Boolean).join(" · ")
    : "Sin ítem seleccionado";
  const supplierName = purchaseItem?.supplierName || "Proveedor no disponible";

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr)",
          sm: "minmax(150px, 1.1fr) minmax(190px, 1.5fr) minmax(105px, .7fr) minmax(105px, .7fr)",
        },
        gap: { xs: 0.8, sm: 1.2 },
        alignItems: "center",
        px: 1.7,
        py: 1.2,
        borderBottom: "1px dashed rgba(64,64,94,.2)",
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap title={ingredient.name} sx={{ fontSize: 13, fontWeight: 800, color: "primary.900" }}>
          {ingredient.name || `Insumo #${ingredient.ingredientId}`}
        </Typography>
        <Typography sx={{ fontSize: 10.5, color: "neutral.600" }}>
          {quantityLabel(ingredient.resolvedQuantity, ingredient.unit)} resueltos
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap title={itemName} sx={{ fontSize: 11.5, fontWeight: 800, color: "primary.900" }}>
          {itemName}
        </Typography>
        <Typography noWrap title={supplierName} sx={{ fontSize: 10.5, color: "neutral.600" }}>
          {supplierName}
        </Typography>
      </Box>
      <Box>
        <Typography sx={{ fontSize: 9.5, color: "neutral.600" }}>Costo unitario</Typography>
        <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: "primary.900" }}>
          {formatCurrency(ingredient.unitCost)}
        </Typography>
      </Box>
      <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
        <Typography sx={{ fontSize: 9.5, color: "neutral.600" }}>Subtotal</Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 900, color: "primary.900" }}>
          {formatCurrency(ingredient.subtotal)}
        </Typography>
      </Box>
    </Box>
  );
}

export default function SaleProductContentModal({
  open,
  product,
  onClose,
}) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [selectedRecipe, setSelectedRecipe] = useState(null);

  useEffect(() => {
    if (!open || !product?.id) {
      setDetail(null);
      setError("");
      setLoading(false);
      setSelectedRecipe(null);
      return undefined;
    }

    const controller = new AbortController();
    setLoading(true);
    setError("");
    getSaleProduct(product.id, { signal: controller.signal })
      .then((response) => {
        if (!controller.signal.aborted) setDetail(response);
      })
      .catch((requestError) => {
        if (!controller.signal.aborted) setError(errorToMessage(requestError));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [open, product?.id, retry]);

  const recipes = Array.isArray(detail?.recipes) ? detail.recipes : [];
  const ingredients = Array.isArray(detail?.ingredients) ? detail.ingredients : [];
  const preparedProducts = Array.isArray(detail?.preparedProducts)
    ? detail.preparedProducts
    : [];
  const resolvedIngredients = Array.isArray(detail?.resolvedIngredients)
    ? detail.resolvedIngredients
    : [];
  const hasContent = recipes.length || ingredients.length || preparedProducts.length;
  const isActive = Boolean(detail?.isActive);
  const costStatus = detail?.costStatus || "REQUIRES_REVIEW";
  const requiresCostReview = costStatus === "REQUIRES_REVIEW";
  const hasEstimatedCost =
    (costStatus === "ESTIMATED" || costStatus === "VALID") &&
    detail?.costCalculatedAt;
  const reviewReasons =
    product?.costReviewReasonsLabel ||
    saleProductReviewReasonsLabel(detail?.costReviewReasons, " · ");
  const showProductDetail = Boolean(open && !selectedRecipe);

  return (
    <>
      <Dialog
      open={showProductDetail}
      onClose={loading ? undefined : onClose}
      maxWidth={false}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      PaperProps={{
        sx: {
          bgcolor: "transparent",
          boxShadow: "none",
          m: 1.5,
          borderRadius: "30px",
          overflow: "visible",
        },
      }}
      slotProps={{
        backdrop: {
          timeout: 180,
          sx: {
            bgcolor: "rgba(2, 8, 23, 0.45)",
            backdropFilter: "blur(3px)",
          },
        },
      }}
    >
      <Fade in={showProductDetail} timeout={180}>
        <Box
          sx={{
            width: "min(96vw, 940px)",
            height: "min(88dvh, 760px)",
            maxHeight: "calc(100dvh - 32px)",
            display: "flex",
            flexDirection: "column",
            bgcolor: "neutral.300",
            borderRadius: "30px",
            p: { xs: 2, sm: 2.5 },
            boxShadow: "0px 20px 60px rgba(0,0,0,.28)",
            overflow: "hidden",
          }}
        >
          <Stack
            direction="row"
            sx={{
              flexShrink: 0,
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.6,
            }}
          >
            <Typography
              noWrap
              title={detail?.name || product?.name}
              sx={{ flex: 1, fontSize: 20, fontWeight: 800, color: "primary.900" }}
            >
              {detail?.name || product?.name || "Producto de venta"}
            </Typography>
            <IconButton type="button" size="small" disabled={loading} onClick={onClose} aria-label="Cerrar contenido">
              <CloseIcon sx={{ color: "primary.900" }} />
            </IconButton>
          </Stack>

          {loading ? (
            <Stack spacing={1} sx={{ minHeight: 260, alignItems: "center", justifyContent: "center" }}>
              <CircularProgress size={28} />
              <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Cargando detalle...</Typography>
            </Stack>
          ) : error ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => setRetry((value) => value + 1)}>
                  Reintentar
                </Button>
              }
            >
              {error}
            </Alert>
          ) : detail ? (
            <Stack
              spacing={2}
              role="region"
              aria-label="Contenido del producto"
              tabIndex={0}
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                overscrollBehavior: "contain",
                p: { xs: 1.6, sm: 2.2 },
                pr: { xs: 1.2, sm: 1.7 },
                borderRadius: "22px",
                bgcolor: "neutral.50",
                boxShadow: "inset 0px 0px 3px rgba(142,142,159,.3)",
                scrollbarGutter: "stable",
                "& > *": { flexShrink: 0 },
              }}
            >
              <SectionTitle>Información general</SectionTitle>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
                  gap: 1,
                }}
              >
                <DataCard label="Sector" value={sectorLabel(detail.sector)} />
                <DataCard label="Precio de venta" value={formatCurrency(detail.price)} />
                <DataCard
                  label="Costo estimado"
                  value={hasEstimatedCost ? formatCurrency(detail.cost) : "—"}
                />
                <DataCard
                  label="Estado del costo"
                  value={saleProductCostStatusLabel(costStatus)}
                />
              </Box>

              <Stack direction="row" spacing={0.7} sx={{ alignItems: "center", px: 0.5 }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "neutral.600" }}>
                  Estado
                </Typography>
                <StatePill positive={isActive}>{isActive ? "Activo" : "Inactivo"}</StatePill>
              </Stack>

              {requiresCostReview ? (
                <Box
                  sx={{
                    px: 1.7,
                    py: 1.2,
                    borderRadius: "14px",
                    bgcolor: "rgba(195, 60, 60, 0.08)",
                    border: "1px solid rgba(195, 60, 60, 0.25)",
                  }}
                >
                  <Typography sx={{ fontSize: 12, fontWeight: 900, color: "error.main" }}>
                    Producto no disponible — el costo requiere revisión
                  </Typography>
                  {reviewReasons ? (
                    <Typography sx={{ mt: 0.3, fontSize: 11, color: "neutral.700" }}>
                      {reviewReasons}
                    </Typography>
                  ) : null}
                </Box>
              ) : null}

              <SectionTitle>Composición directa</SectionTitle>
              <Box
                data-testid="direct-composition-list"
                sx={{
                  borderRadius: "18px",
                  bgcolor: "secondary.50",
                  overflow: "hidden",
                }}
              >
                {ingredients.map((ingredient) => (
                  <ContentRow
                    key={`ingredient:${ingredient.ingredientId}`}
                    name={ingredient.name || `Insumo #${ingredient.ingredientId}`}
                    detail={quantityLabel(ingredient.quantity, ingredient.unit)}
                    type="Insumo"
                  />
                ))}
                {preparedProducts.map((preparedProduct) => (
                  <ContentRow
                    key={`prepared:${preparedProduct.preparedProductId}`}
                    name={preparedProduct.name || `Elaborado #${preparedProduct.preparedProductId}`}
                    detail={quantityLabel(preparedProduct.quantity, preparedProduct.resultUnit)}
                    type="Elaborado"
                  />
                ))}
                {recipes.map((recipe) => (
                  <ContentRow
                    key={`recipe:${recipe.recipeId}`}
                    name={recipe.name || `Receta #${recipe.recipeId}`}
                    detail={quantityLabel(recipe.quantity, "portion")}
                    type="Receta"
                    action={
                      <Button
                        type="button"
                        size="small"
                        onClick={() =>
                          setSelectedRecipe({
                            id: recipe.recipeId,
                            name: recipe.name,
                          })
                        }
                        sx={{
                          width: 96,
                          minWidth: 96,
                          py: 0.55,
                          px: 0.8,
                          borderRadius: "20px",
                          bgcolor: "primary.500",
                          color: "logoblanco",
                          textTransform: "none",
                          fontSize: 11,
                          fontWeight: 800,
                          boxShadow: "inset 0px 0px 3px rgba(142,142,159,.35)",
                          "&:hover": { bgcolor: "primary.600" },
                        }}
                      >
                        Ver receta
                      </Button>
                    }
                  />
                ))}
                {!hasContent ? (
                  <Typography sx={{ px: 2, py: 3, fontSize: 12, color: "neutral.600" }}>
                    Este producto no tiene contenido para mostrar.
                  </Typography>
                ) : null}
              </Box>

              <SectionTitle>Desglose de costo</SectionTitle>
              <Box sx={{ borderRadius: "18px", bgcolor: "secondary.50", overflow: "hidden" }}>
                <Box
                  data-testid="resolved-ingredients-list"
                  sx={{
                    overflow: "hidden",
                  }}
                >
                  {resolvedIngredients.map((ingredient) => (
                    <ResolvedIngredientRow
                      key={`resolved:${ingredient.ingredientId}`}
                      ingredient={ingredient}
                    />
                  ))}
                  {!resolvedIngredients.length ? (
                    <Typography sx={{ px: 2, py: 3, fontSize: 12, color: "neutral.600" }}>
                      No hay insumos consolidados para mostrar.
                    </Typography>
                  ) : null}
                </Box>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={0.5}
                  sx={{
                    alignItems: { xs: "flex-start", sm: "center" },
                    justifyContent: "space-between",
                    px: 1.7,
                    py: 1.4,
                    bgcolor: "secondary.100",
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 800, color: "primary.900" }}>
                      Costo total
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: "neutral.600" }}>
                      {detail.costCalculatedAt
                        ? `Calculado el ${dateLabel(detail.costCalculatedAt)}`
                        : "A la espera del cálculo automático"}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 18, fontWeight: 900, color: "primary.900" }}>
                    {hasEstimatedCost
                      ? formatCurrency(detail.cost)
                      : saleProductCostStatusLabel(costStatus)}
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          ) : null}
        </Box>
      </Fade>
      </Dialog>

      <SaleProductRecipeDetailModal
        open={Boolean(selectedRecipe)}
        recipeId={selectedRecipe?.id}
        recipeName={selectedRecipe?.name}
        onClose={() => setSelectedRecipe(null)}
      />
    </>
  );
}
